import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { MaskLines, Reveal, Eyebrow } from "../components/site/Reveal";
import { PhotoPlaceholder } from "../components/site/PhotoPlaceholder";
import { Marquee } from "../components/site/Marquee";
import { DishCard } from "../components/site/DishCard";
import { HoursPanel } from "../components/site/HoursPanel";
import { ContactCard } from "../components/site/ContactActions";
import { OfferBanner } from "../components/site/OfferBanner";
import { restaurant, formatPrice } from "../data/siteData";
import { useContent, usePizzas, useHours } from "../lib/content";
import { useCart } from "../lib/cart";
import { summarizeHours } from "../lib/hours";

const HeroPhoto = () => {
  const ref = useRef(null);
  const hours = summarizeHours(useHours());
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <motion.div ref={ref} style={{ y }} className="relative [perspective:1200px]" onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }}>
      <motion.div
        style={{ rotateX: rx, rotateY: ry }}
        initial={{ clipPath: "inset(100% 0 0 0 round 28px)" }}
        animate={{ clipPath: "inset(0% 0 0 0 round 28px)" }}
        transition={{ duration: 1.4, delay: 0.35, ease: [0.76, 0, 0.24, 1] }}
      >
        <PhotoPlaceholder src={restaurant.heroImage} alt="CAP SUD" label="Photo principale" sub="À ajouter par le restaurant" className="aspect-[4/5] rounded-[28px] border border-[#D4A359]/30" testId="hero-photo-slot" />
      </motion.div>
      {hours[0] && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.8 }} className="absolute -left-4 sm:-left-10 bottom-8 rounded-2xl border border-[#D4A359]/30 bg-[#0B2216]/90 backdrop-blur-md px-5 py-4 shadow-2xl">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#D4A359]">{hours[0].days}</p>
          <p className="mt-1 text-sm text-[#F7F4EB]">{hours[0].text}</p>
        </motion.div>
      )}
    </motion.div>
  );
};

const Hero = () => (
  <section className="relative pt-32 sm:pt-36 pb-20 lg:pb-28 overflow-hidden" data-testid="home-hero">
    <div className="glow-orb" aria-hidden="true" />
    <div className="container-site relative grid gap-14 lg:grid-cols-12 lg:items-end">
      <div className={restaurant.heroImage ? "lg:col-span-7" : "lg:col-span-12"}>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}>
          <Eyebrow>Restaurant · Pizzeria · Morangis — depuis {restaurant.since}</Eyebrow>
        </motion.div>
        <MaskLines
          testId="hero-title"
          lines={["CAP", <span key="s" className="italic text-[#E5B66C] pl-[14vw] sm:pl-[10vw] lg:pl-28">Sud</span>]}
          className="mt-6 font-display font-semibold leading-[0.82] tracking-tight text-[30vw] sm:text-[24vw] lg:text-[11.5rem] xl:text-[13.5rem] text-[#F7F4EB]"
        />
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.7 }}>
          <p className="mt-10 max-w-xl font-display text-2xl sm:text-3xl leading-snug text-[#F7F4EB]" data-testid="hero-intro">
            Des pizzas à emporter, des plats variés et un accueil convivial <em className="text-[#E5B66C]">depuis 2004.</em>
          </p>
          <div className="mt-10 flex flex-col sm:flex-row sm:flex-wrap gap-3">
            <Link to="/notre-carte" className="btn-gold" data-testid="hero-cta-carte">Découvrir la carte <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/commander-a-emporter" className="btn-ghost btn-terracotta" data-testid="hero-cta-commander">Commander à emporter <ArrowUpRight className="w-4 h-4" /></Link>
            <Link to="/location-de-salle" className="btn-ghost" data-testid="hero-cta-salle">Location de salle</Link>
          </div>
        </motion.div>
      </div>
      {restaurant.heroImage && (
        <div className="lg:col-span-5 max-w-md w-full mx-auto lg:max-w-none">
          <HeroPhoto />
        </div>
      )}
    </div>
  </section>
);

const Bento = () => {
  const pizzas = usePizzas();
  const prices = pizzas.map((p) => p.price).filter((p) => p != null);
  return (
    <section className="py-24 sm:py-32" data-testid="specialites-section">
      <div className="container-site">
        <Reveal className="grid gap-8 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-8">
            <Eyebrow>Chez CAP SUD</Eyebrow>
            <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl leading-tight">Pizzas, plats variés <em className="text-[#E5B66C]">et bonne humeur.</em></h2>
          </div>
          <p className="lg:col-span-4 font-display italic text-7xl sm:text-8xl text-[#D4A359]/25 lg:text-right" aria-hidden="true">2004</p>
        </Reveal>
        <div className="mt-14 grid gap-5 lg:grid-cols-12 lg:grid-rows-2">
          <Reveal className="lg:col-span-7 lg:row-span-2">
            <Link to="/notre-carte" className="bento-tile group h-full min-h-[420px] lg:min-h-[560px] flex flex-col" data-testid="bento-carte">
              <div className="absolute inset-0 transition-transform duration-[1200ms] group-hover:scale-105">
                <PhotoPlaceholder src={restaurant.carteTileImage} label="Photo — la carte" className="h-full w-full" />
              </div>
              <div className="relative mt-auto p-7 sm:p-10 bg-gradient-to-t from-[#0B2216] via-[#0B2216]/85 to-transparent">
                <p className="text-xs uppercase tracking-[0.3em] text-[#D4A359]">Pizzas · Pâtes · Viandes · Poissons · Desserts</p>
                <h3 className="mt-3 font-display text-5xl sm:text-6xl">Notre carte</h3>
                <span className="mt-5 inline-flex items-center gap-2 group-hover:gap-4 transition-[gap] duration-300">Feuilleter la carte <ArrowRight className="w-4 h-4" /></span>
              </div>
            </Link>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={0.1}>
            <Link to="/nos-pizzas" className="bento-tile group h-full min-h-[260px] flex flex-col justify-end p-7 sm:p-10" data-testid="bento-pizzas">
              <p className="text-xs uppercase tracking-[0.3em] text-[#D4A359]">{pizzas.length ? `${pizzas.length} recettes` : "Nos recettes"}{prices.length ? ` · dès ${formatPrice(Math.min(...prices))}` : ""}</p>
              <h3 className="mt-3 font-display text-4xl sm:text-5xl">Nos pizzas</h3>
              <span className="mt-5 inline-flex items-center gap-2 group-hover:gap-4 transition-[gap] duration-300">Voir les pizzas <ArrowRight className="w-4 h-4" /></span>
            </Link>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={0.2}>
            <Link to="/location-de-salle" className="bento-tile bento-terracotta group h-full min-h-[260px] flex flex-col justify-end p-7 sm:p-10" data-testid="bento-salle">
              <p className="text-xs uppercase tracking-[0.3em] text-[#F7F4EB]/85">Anniversaires · Mariages · Fêtes de famille</p>
              <h3 className="mt-3 font-display text-4xl sm:text-5xl">Location de salle</h3>
              <span className="mt-5 inline-flex items-center gap-2 group-hover:gap-4 transition-[gap] duration-300">Demander un devis <ArrowUpRight className="w-4 h-4" /></span>
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const Featured = () => {
  const { content } = useContent();
  const pizzas = usePizzas();
  const { add } = useCart();
  const ids = Array.isArray(content?.featured) ? content.featured : [];
  const items = ids.map((id) => pizzas.find((p) => p.id === id)).filter(Boolean);
  if (!items.length) return null;
  const onAdd = (p) => {
    add(p.id);
    toast.success(`${p.name} ajoutée au panier`);
  };
  return (
    <section className="pb-24 sm:pb-32" data-testid="featured-section">
      <div className="container-site">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
          <Reveal>
            <Eyebrow>À emporter</Eyebrow>
            <h2 className="mt-5 font-display text-4xl sm:text-5xl">Quelques pizzas</h2>
          </Reveal>
          <Link to="/nos-pizzas" className="btn-ghost self-start sm:self-auto" data-testid="featured-see-all">Toutes les pizzas <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="mt-12 grid gap-5 sm:gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((d, i) => <DishCard key={d.id} dish={d} index={i} onAdd={onAdd} />)}
        </div>
      </div>
    </section>
  );
};

const InfoSection = () => (
  <section className="py-24 sm:py-32 bg-[#0E2A1B] border-t border-[#D4A359]/15" data-testid="home-info-section">
    <div className="container-site">
      <Reveal>
        <Eyebrow>Horaires & coordonnées</Eyebrow>
        <h2 className="mt-5 font-display text-4xl sm:text-5xl">Passez nous voir à Morangis</h2>
      </Reveal>
      <div className="mt-12 grid gap-5 lg:grid-cols-2">
        <Reveal><HoursPanel testId="home-hours-panel" /></Reveal>
        <Reveal delay={0.1} className="h-full"><ContactCard testId="home-contact-card" /></Reveal>
      </div>
    </div>
  </section>
);

export default function HomePage() {
  return (
    <>
      <Hero />
      <OfferBanner testId="home-offer-banner" />
      <Marquee />
      <Bento />
      <Featured />
      <InfoSection />
    </>
  );
}
